import * as React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getAliasFromType, upperCaseFirst } from '../../../../utils/Utils';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import AsyncButton from '../../AsyncButton';
import Modal from 'antd/lib/modal';
import * as Yup from 'yup';
import { Link, useParams } from 'react-router-dom';
import useOrg from '../../../../services/hooks/useOrg';
import { useEntityCreate, useUserEntities } from '../../../../services/hooks/useEntity';
import { faSpinnerThird } from '@fortawesome/pro-solid-svg-icons';
import { faPlus } from '@fortawesome/pro-light-svg-icons';

const EntityCreateSchema = Yup.object().shape({
  name: Yup.string().label('Name').required(),
});

export interface CreateEntityModalProps {
  visible: boolean;
  toggleCreateEntity: () => void;
  entityType: string;
  onSuccess: () => void;
}

function CreateEntityModal({ visible, toggleCreateEntity, onSuccess, entityType }: CreateEntityModalProps) {
  const { orgPath = '' } = useParams();
  const { data: org } = useOrg({ orgPath });
  const { data: entities = [], isLoading: isEntitiesLoading } = useUserEntities(org?._id);
  const entityLimitReached = entities.length >= org?.metadata[`${entityType}Limit`];
  const orgIsEnterprise = org?.isEnterprise;
  const { isPending, isSuccess, isError, mutateAsync: createEntity } = useEntityCreate(org?._id, orgPath, true);

  const handleSubmit = ({ name }: any) => {
    createEntity({ name, entityType }).then(() => onSuccess());
  };

  const renderBillingInfo = () => {
    if (!org?._id || isEntitiesLoading)
      return <FontAwesomeIcon icon={faSpinnerThird} size="lg" className="fa-fast-spin margin-bottom-sm full-width" />;

    if (orgIsEnterprise && entityLimitReached) {
      return (
        <div className="margin-bottom-sm text-error">
          You have reached the limit of {entityType}s for your organization. To add more {entityType}s please contact
          support.
        </div>
      );
    }

    return null;
  };

  return (
    <Modal
      open={visible}
      title={
        <span className="flex-container align-center">
          <FontAwesomeIcon icon={faPlus} className="margin-right-sm" />
          Create a new {upperCaseFirst(entityType === 'competition' ? 'challenge' : getAliasFromType(entityType))}
        </span>
      }
      footer={null}
      onCancel={toggleCreateEntity}
      destroyOnClose={true}
    >
      <div className="form-container padding-hsides-sm padding-bottom-sm">
        {renderBillingInfo()}
        {isError ? (
          <div className="invalid-feedback text-center margin-bottom-md">
            Could not create {entityType}, please try again or contact support.
          </div>
        ) : (
          ''
        )}
        <Formik
          initialValues={{ name: '' }}
          validateOnChange={false}
          validateOnBlur={true}
          validationSchema={EntityCreateSchema}
          onSubmit={handleSubmit}
          render={() => (
            <Form className="form-container">
              <div className="form-group">
                <label htmlFor="name">
                  {upperCaseFirst(entityType === 'competition' ? 'challenge' : getAliasFromType(entityType))} Name
                </label>
                <div className="input-group">
                  <Field name="name" className="form-control" />
                </div>
                <ErrorMessage name="name">{(msg: string) => <div className="invalid-feedback">{msg}</div>}</ErrorMessage>
              </div>
              <div className="margin-top-md flex-container justify-end">
                <button type="button" className="btn margin-right-xs" onClick={toggleCreateEntity}>
                  Cancel
                </button>
                <button type="button" className="btn margin-right-xs">
                  <Link to="/public/idea" className="btn" style={{ padding: 0 }}>
                    Success Checker
                  </Link>
                </button>
                <AsyncButton
                  type="submit"
                  className="btn btn-primary"
                  asyncState={isPending ? 'REQUEST' : isSuccess ? 'SUCCESS' : ''}
                  disabled={orgIsEnterprise && entityLimitReached}
                >
                  Create {upperCaseFirst(entityType === 'competition' ? 'challenge' : getAliasFromType(entityType))}
                </AsyncButton>
              </div>
            </Form>
          )}
        />
      </div>
    </Modal>
  );
}

export default React.memo(CreateEntityModal);
